import {Modal} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import type {AppError} from '@packages/utils';
import {useState} from 'react';
import type {CommunityComment} from '../../../../../core/entities/community/schema';
import {usePostAlertCommunityComment} from '../../../../../core/repositories/communityRepository';
import {queryClient} from '../../../../../lib/react-query';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../../../shared/components';
import {formatDateFullWithTime} from '../../../../../shared/services/formatDate';
import {LastConfirmModal} from './LastConfirmModal';

type Props = {
  opened: boolean;
  open: () => void;
  close: () => void;
  topicId: number;
  comment: CommunityComment;
};

type CastedError = {message: AppError};

export const ReportCommentModal: React.FC<Props> = ({
  opened,
  open,
  close,
  topicId,
  comment,
}) => {
  const [
    isLastConfirmModalOpened,
    {open: openLastConfirmModal, close: closeLastConfirmModal},
  ] = useDisclosure(false);
  const [isReportLoading, setIsReportLoading] = useState(false);

  const reportComment = usePostAlertCommunityComment({
    topicId,
    seqNumber: comment.seqNumber,
    onMutateFnc() {
      setIsReportLoading(true);
    },
    async onSuccessFnc() {
      showSuccessSnackBar({title: '送信しました', message: 'done'});
      await queryClient.invalidateQueries({
        queryKey: ['getCommunityCommentList'],
      });
      closeLastConfirmModal();
    },
    onErrorFnc(error: unknown) {
      const castedError = error as CastedError;
      showErrorSnackBar({
        isNetworkError: castedError.message === 'NetworkError',
      });
    },
    onSettledFnc() {
      setIsReportLoading(false);
    },
  });

  const onSubmit = () => {
    close();
    openLastConfirmModal();
  };

  const onLastConfirmSubmit = async () => {
    reportComment.mutate();
  };

  const onLastConfirmClose = () => {
    closeLastConfirmModal();
    open();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        withCloseButton={false}
        size={668}
        padding={0}
        radius={12}
        centered={true}
      >
        <div className="p-24">
          <p className="text-md text-black-1 font-bold leading">
            不適切な内容の通報
          </p>
          <p className="text-base text-black-1 leading mt-16">
            回答No.{comment.seqNumber}を不適切な内容として通報しますか？
          </p>
          <div className="bg-background-1 p-16 rounded-4 mt-8">
            <div className="flex justify-between items-center">
              <div className="flex justify-start items-center">
                <p className="text-base text-black-2 font-bold leading ml-4">
                  {comment.seqNumber}
                </p>
                <p className="text-base text-black-2 font-bold leading ml-4">
                  {comment.nickname}
                </p>
              </div>
              <p className="text-sm text-black-2 font-bold leading ml-8">
                {formatDateFullWithTime(comment.createdAt)}
              </p>
            </div>
            <div className="mt-12">
              <p className="text-base text-black-1 leading">{comment.body}</p>
            </div>
          </div>
          <div className="flex justify-center items-center mt-16">
            <button
              onClick={close}
              className="w-[128px] bg-black-4 py-12 px-24 text-base text-white leading font-bold rounded-full hover:opacity-80 mr-24"
            >
              キャンセル
            </button>
            <button
              onClick={onSubmit}
              className="w-[128px] bg-notification-1 py-12 px-24 text-base text-white leading font-bold rounded-full hover:opacity-80 mr-24"
            >
              通報する
            </button>
          </div>
        </div>
      </Modal>
      <LastConfirmModal
        opened={isLastConfirmModalOpened}
        onSubmit={onLastConfirmSubmit}
        onClose={onLastConfirmClose}
        isLoading={isReportLoading}
        isNotification={true}
      />
    </>
  );
};
